import React, { useState, useContext } from "react";
import { Image, Button, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShareFromSquare,
  faEdit,
  faTrash,
  faCog,
  faHeart as faHeartSolid,
  faImages,
  faScroll,
  faCircleQuestion,
  faUsers
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import ShareModal from "./VisualModals/Share";
import DeleteModal from "./VisualModals/Delete";
import QuizEditModal from "./QuizEditModal";
import './CarouselVisCard.css';
import defaultThumbnail from '../assets/atom_science_logo.png';
import { APIEditPresentations } from "../helper/APIFunctions";
import { UserInfo } from "../helper/Context";
import { getColorForFolder, buildFolderTree } from "../helper/Utils";
const RenderFolderItems = ({ folders, onSelect, selectedFolder, level = 0 }) => {
  const prefix = "\u00A0\u00A0\u00A0\u00A0".repeat(level);
  return folders.map(folder => (
    <React.Fragment key={folder.id}>
      <Dropdown.Item onClick={() => onSelect(folder.id)}>
        {prefix}{folder.title}
      </Dropdown.Item>
      {folder.children && folder.children.length > 0 && (
        <RenderFolderItems folders={folder.children} onSelect={onSelect} selectedFolder={selectedFolder} level={level + 1} />
      )}
    </React.Fragment>
  ));
};

const CarouselPresentationCard = ({ presentation, onPresDelete, onPresEdit, isOrg, folders = [], folderTree = [], lockoutEdit = false, lockoutDelete = false, blockActions = false }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQuizEditModal, setShowQuizEditModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(presentation.favorite);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { userInfo } = useContext(UserInfo);
  const canEdit = (userInfo?.organization_permissions?.edit_presentations || !isOrg) && !lockoutEdit && !blockActions;
  const canDelete = (userInfo?.organization_permissions?.delete_presentations || !isOrg) && !lockoutDelete && !blockActions;
  // Detect quiz presentations regardless of where is_quiz is stored on the backend
  const isQuiz = ((presentation.slides.length ==  1) && (presentation.slides[0].type === 'quiz'));
  const handleToggleFavorite = () => {
    if (!canEdit) return;
    const newFavoriteStatus = !isFavorite;
    setIsFavorite(newFavoriteStatus);
    APIEditPresentations(
      presentation.id, { favorite: newFavoriteStatus }, isOrg
    ).then(() => {
        onPresEdit({ ...presentation, favorite: newFavoriteStatus });
    }).catch((err) => {
        setIsFavorite(!newFavoriteStatus); // Revert on error
    });
  };

  const handleMoveFolder = (newFolderId) => {
    if (!canEdit) return;
    if (newFolderId === presentation.folder_id) return;
    APIEditPresentations(
        presentation.id, { folder_id: newFolderId }, isOrg
    ).then(() => {
      onPresEdit({ ...presentation, folder_id: newFolderId });
    });
  };

  const handlePreview = (e) => {
    e.preventDefault();
    window.open(presentation.url, "_blank");
  };
  const folderName = folders.find(f => f.id === presentation.folder_id)?.title;
  const folderColor = getColorForFolder(presentation.folder_id, folderName);
  return (
    <>
      <div className={`carousel-vis-card ${isDropdownOpen ? 'dropdown-open' : ''}`}>
        <a 
          href={presentation.url} 
          onClick={handlePreview}
          className="carousel-vis-card-thumbnail-container"
        >
          <OverlayTrigger
            placement="top"
            delay={{ show: 1500, hide: 250 }}
            overlay={
              <Tooltip id={`tooltip-type-${presentation.id}`}>
                {isQuiz ? "Quiz" : presentation.type === "scrollytelling" ? "Scrollytelling Story" : "Slideshow Presentation"}
              </Tooltip>
            }
          >
            <span className="primary-card-overlay-icon">
              <FontAwesomeIcon icon={isQuiz ? faCircleQuestion : presentation.type === "scrollytelling" ? faScroll : faImages} />
          </span>
          </OverlayTrigger>
          {isOrg && (
            <OverlayTrigger
              placement="top"
              delay={{ show: 1500, hide: 250 }}
              overlay={<Tooltip id={`tooltip-team-${presentation.id}`}>Team Presentation</Tooltip>}
            >
              <span className="secondary-card-overlay-icon">
              <FontAwesomeIcon icon={faUsers} />
              </span>
            </OverlayTrigger>
          )}
          <Image src={presentation.thumbnail || defaultThumbnail} className={`carousel-vis-card-thumbnail ${!presentation.thumbnail ? 'no-thumbnail' : ''}`} />
          {presentation.description ? <div className="carousel-vis-card-description">{presentation.description}</div> : null}
        </a>
        <div className="carousel-vis-card-body">
            <div className="d-flex justify-content-between align-items-start">
              <OverlayTrigger
                placement="top"
                delay={{ show: 1500, hide: 250 }}
                overlay={<Tooltip id={`tooltip-title-${presentation.id}`}>{presentation.title}</Tooltip>}
              >
                <h5 className="carousel-vis-card-title">{presentation.title}</h5>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                delay={{ show: 1500, hide: 250 }}
                overlay={<Tooltip id={`tooltip-favorite-${presentation.id}`}>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</Tooltip>}
              >
                <Button onClick={handleToggleFavorite} className="carousel-card-icon-button favorite-overlay-button" disabled={!canEdit}
                style={{
                  cursor: !canEdit ? 'default' : 'pointer',
                  pointerEvents: !canEdit ? 'none' : 'auto'
                }}>
                        <FontAwesomeIcon icon={isFavorite ? faHeartSolid : faHeartRegular} />
                </Button>
              </OverlayTrigger>
            </div>
          
          <div className="carousel-vis-card-meta mb-2">{(!canEdit) ?
            (<div className="vis-card-folder-label" style={{ borderLeft: `5px solid ${folderColor}`, pointerEvents: 'none' }}>
              {folderName || 'Uncategorized'}
            </div>)
            : (
            <Dropdown onToggle={setIsDropdownOpen}>
              <Dropdown.Toggle as="div" className="vis-card-folder-label" style={{ borderLeft: `5px solid ${folderColor}` }}>
                {presentation.folder_id ? folderName : 'Uncategorized'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleMoveFolder(null)} disabled={!presentation.folder_id}>
                  Uncategorized
                </Dropdown.Item>
                <RenderFolderItems folders={folderTree || buildFolderTree(folders)} onSelect={handleMoveFolder} selectedFolder={presentation.folder_id} />
              </Dropdown.Menu>
            </Dropdown>
          )}
          </div>

          <div className="carousel-vis-card-actions">
            <OverlayTrigger
              placement="top"
              delay={{ show: 1500, hide: 250 }}
              overlay={<Tooltip id={`tooltip-share-${presentation.id}`}>Share</Tooltip>}
            >
            <span
              role="button"
              tabIndex="0"
              onClick={() => setShowShareModal(true)}
              onKeyDown={(e) => e.key === 'Enter' && setShowShareModal(true)}
              className="card-action-icon"
            >
              <FontAwesomeIcon icon={faShareFromSquare} />
            </span>
            </OverlayTrigger>
            <Dropdown onToggle={setIsDropdownOpen}>
              <OverlayTrigger
                placement="top"
                delay={{ show: 1500, hide: 250 }}
                overlay={<Tooltip id={`tooltip-settings-${presentation.id}`}>Settings</Tooltip>}
              >
                <Dropdown.Toggle as="span" className="card-action-icon" id="dropdown-basic">
                    <FontAwesomeIcon icon={faCog} />
                </Dropdown.Toggle>
              </OverlayTrigger>
                <Dropdown.Menu>
                    <Dropdown.Item
                        onClick={() => {
                          if (isQuiz) {
                            setShowQuizEditModal(true);
                          } else {
                            navigate(
                              presentation.type === "scrollytelling" ? "/scrollytelling/builder" : "/create-presentation",
                              { state: { presentation: presentation } }
                            );
                          }
                        }}
                        disabled={!canEdit || presentation.type === 'slideshow'}
                    >
                        <FontAwesomeIcon icon={faEdit} className="me-2" /> Edit
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => setShowDeleteModal(true)} className="text-danger" disabled={!canDelete}>
                        <FontAwesomeIcon icon={faTrash} className="me-2" /> Delete
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {canEdit && isQuiz && showQuizEditModal && (
        <QuizEditModal
          show={showQuizEditModal}
          onHide={() => setShowQuizEditModal(false)}
          presentation={presentation}
          onSaved={(updated) => onPresEdit(updated)}
          isOrg={isOrg}
        />
      )}
      {showShareModal && <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        url={presentation.url}
        qr={presentation.qr_url}
        id={presentation.id}
        isPresentation={true}
      />}
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        vis={presentation}
        onVisDelete={() => onPresDelete(presentation.id)}
        isOrg={isOrg}
        type="presentation"
      />
    </>
  );
};

export default CarouselPresentationCard; 